import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FC, Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "sonner";
import {
  useWalletObserver,
  useWalletLoadingState,
} from "@sundaeswap/wallet-lite";

import { ProvidersWrapper } from "./components/ProvidersWrapper";
import { GummiwormBalance } from "./components/GummiwormBalance";
import { Header } from "./components/Header";
import { WalletBalance } from "./components/WalletBalance";
import { DepositOverlay } from "./components/DepositOverlay";
import { useGummiwormContext } from "./context/GummiwormContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    },
  },
});

const ErrorFallback = ({ error }: { error: Error }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="p-6 rounded-xl bg-sr-surface max-w-sm mx-4">
      <h2 className="text-lg font-medium text-white mb-3">
        Something went wrong
      </h2>
      <pre className="text-xs text-sr-salmon bg-sr-salmon/10 p-3 rounded-lg overflow-auto">
        {error.message}
      </pre>
    </div>
  </div>
);

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-6 h-6 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
  </div>
);

const MainContent: FC = () => {
  const { activeWallet } = useWalletObserver();
  const { ready } = useWalletLoadingState();

  const isConnected = !!activeWallet;
  const { accounts, depositTxs } = useGummiwormContext();
  const hasL2Funds = accounts.length > 0;
  const hasPendingDeposits = depositTxs.size > 0;
  const [depositOpen, setDepositOpen] = useState(false);
  const [viewRequestId, setViewRequestId] = useState<string | null>(null);
  const [autoOpenChecked, setAutoOpenChecked] = useState(false);

  // Auto-open the deposit overlay only for brand new users:
  // - Wait for wallet state to settle (ready or confirmed not connecting)
  // - Don't open if: wallet connected, has L2 funds, or has pending deposits
  useEffect(() => {
    if (autoOpenChecked) return;
    // Wait until wallet loading state has resolved
    if (ready || isConnected) {
      if (!hasL2Funds && !hasPendingDeposits) {
        setDepositOpen(true);
      }
      setAutoOpenChecked(true);
      return;
    }
    // If wallet-lite has finished its initial check and nothing connected
    // (willAutoConnect would have set ready=true), open for new users
    const timer = setTimeout(() => {
      if (!isConnected) {
        setDepositOpen(true);
      }
      setAutoOpenChecked(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [isConnected, ready, hasL2Funds, hasPendingDeposits, autoOpenChecked]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 flex flex-col items-center px-4 py-6 sm:py-12">
        <div className="w-full max-w-md flex flex-col gap-6">
          {/* Wallet section */}
          <WalletBalance onDeposit={() => setDepositOpen(true)} />

          {/* Deposit history */}
          <GummiwormBalance
            onViewDeposit={(id) => {
              setViewRequestId(id);
              setDepositOpen(true);
            }}
          />
        </div>
      </main>

      <DepositOverlay
        open={depositOpen}
        onClose={() => {
          setDepositOpen(false);
          setViewRequestId(null);
        }}
        viewRequestId={viewRequestId}
      />

      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "var(--color-sr-surface)",
            color: "var(--color-sr-oat-milk)",
            border: "1px solid var(--color-sr-border-subtle)",
          },
        }}
      />
    </div>
  );
};

export const App: FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ProvidersWrapper>
            <MainContent />
          </ProvidersWrapper>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  );
};
